'use client';

import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { useRouter } from 'next/router';
import { useEffect, useState } from 'react';

import { useAuth } from '@app/hooks/useAuth';
import { useLayout } from '@app/hooks/useLayout';
import { useWindow } from '@app/hooks/useWindow';
import { useMainNavigation } from '@hooks/useCommonData';
import { cn } from '@libs/utils';

import ArrowDownIcon from '../icons/ArrowDownIcon';
import PlusIcon from '../icons/PlusIcon';
import LogoImage from '../LogoImage';
import NotificationBadge from '../ui/NotificationBadge';

import { AuthMenu } from './AuthMenu';

interface HeaderProps {
  className?: string;
  showFull?: boolean;
  enableBackButton?: boolean;
}

export default function Header({
  className = '',
  showFull = true,
  enableBackButton = false,
}: HeaderProps) {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const { isAtTop } = useWindow();
  const navigationItems = useMainNavigation();
  const path = usePathname();
  const { showHeader } = useLayout();
  const router = useRouter();

  const [activeItem, setActiveItem] = useState<string>();
  useEffect(() => {
    if (path === null) return;

    const segments = path.split('/');
    const activeItem = navigationItems.find(item => segments[1] === item.href.split('/')[1]);
    setActiveItem(activeItem?.href);
  }, [navigationItems, path]);

  if (!showHeader) return null;

  return (
    <header
      className={cn(
        'w-full bg-white sticky top-0 z-[9999] transition-all duration-300',
        className,
        !isAtTop && 'shadow-xl'
      )}
    >
      <div className="2xl:w-[90rem] w-full mx-auto px-5 relative">
        <div
          className={cn('flex items-center h-20', showFull ? 'justify-between' : 'justify-center')}
        >
          <div className="flex items-center gap-2.5">
            {enableBackButton && (
              <button
                onClick={() => router.back()}
                className="p-2 text-gray-600 hover:text-blue-800/20 transition-colors duration-200 rotate-90 absolute left-4 cursor-pointer"
              >
                <ArrowDownIcon className="w-6 h-6" />
              </button>
            )}
            <Link href="/" className="flex items-center gap-1">
              <LogoImage variant="full" size="md" theme="light" className="h-8 w-auto" />
            </Link>
          </div>
          {showFull && (
            <>
              <nav className="hidden lg:flex items-center gap-8">
                {navigationItems.map((item, index) => (
                  <Link
                    key={item.label}
                    href={item.href}
                    className={cn(
                      'px-0 py-0 text-sm font-bold transition-colors duration-200',
                      activeItem === item.href || (!activeItem && index === 0)
                        ? 'text-blue-800'
                        : 'text-gray-900 hover:text-blue-800'
                    )}
                  >
                    {item.label}
                  </Link>
                ))}
              </nav>
              <div className="flex items-center gap-4">
                <div className="hidden lg:flex items-center gap-4">
                  <AuthMenu />
                </div>

                <div className="lg:hidden">
                  <button
                    onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
                    className="p-2 text-gray-600 hover:text-blue-800 transition-colors duration-200"
                  >
                    <svg
                      className="w-6 h-6"
                      fill="none"
                      stroke="currentColor"
                      viewBox="0 0 24 24"
                      xmlns="http://www.w3.org/2000/svg"
                    >
                      {isMobileMenuOpen ? (
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M6 18L18 6M6 6l12 12"
                        />
                      ) : (
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M4 6h16M4 12h16M4 18h16"
                        />
                      )}
                    </svg>
                  </button>
                </div>
                <NotificationBadge />
              </div>
            </>
          )}
        </div>

        {showFull && isMobileMenuOpen && (
          <div className="lg:hidden border-t border-gray-200 fixed top-20 left-0 right-0 bottom-0 bg-white z-[9999] px-5 md:px-10 py-5 md:py-10 flex flex-col overflow-y-auto">
            <nav className="flex flex-col space-y-2 grow-1">
              {navigationItems.map(item => (
                <Link
                  key={item.label}
                  href={item.href}
                  className={cn(
                    'md:px-4 py-2 text-base font-bold uppercase transition-colors duration-200',
                    item.active ? 'text-blue-800' : 'text-gray-900 hover:text-blue-800'
                  )}
                  onClick={() => setIsMobileMenuOpen(false)}
                >
                  {item.label}
                </Link>
              ))}
            </nav>

            <div className="mt-4 pt-4 border-t border-gray-200 flex flex-wrap gap-4">
              <AuthMenu isSimple />
            </div>
          </div>
        )}
      </div>
    </header>
  );
}
